import { db } from "./db-client";
import { getRootUrl, getAppUrl, getCookieDomain } from "./subdomain";
import type { AuthSession as Session, AuthUser as User } from "./auth-client";
import Cookies from 'js-cookie';
import { auth } from "./auth-client";
import { logoutOneSignal } from "./onesignal";

/**
 * Authentication helper utilities to standardize auth checks across the app
 */

export interface AuthUser {
  id: string;
  email?: string;
}

export interface AuthResult {
  user: User;
  session: Session;
}

/**
 * Get the current authenticated user
 * @throws Error if not authenticated
 * @returns Promise<User> The authenticated user
 */
export async function getCurrentUser(): Promise<User> {
  const {
    data: { user },
    error,
  } = await auth.getUser();

  if (error) {
    throw new Error(`Authentication error: ${error.message}`);
  }

  if (!user) {
    throw new Error("Not authenticated");
  }

  return user;
}

/**
 * Get the current authenticated user, returns null if not authenticated
 * @returns Promise<User | null> The authenticated user or null
 */
export async function getCurrentUserOrNull(): Promise<User | null> {
  try {
    const {
      data: { user },
      error,
    } = await auth.getUser();

    if (error || !user) {
      return null;
    }

    return user;
  } catch {
    return null;
  }
}

/**
 * Get the current session
 * @throws Error if no session exists
 * @returns Promise<Session> The current session
 */
export async function getAuthSession(): Promise<Session> {
  const {
    data: { session },
    error,
  } = await auth.getSession();

  if (error) {
    throw new Error(`Session error: ${error.message}`);
  }

  if (!session) {
    throw new Error("No active session");
  }

  return session;
}

/**
 * Get the current session, returns null if no session exists
 * @returns Promise<Session | null> The current session or null
 */
export async function getAuthSessionOrNull(): Promise<Session | null> {
  try {
    const {
      data: { session },
      error,
    } = await auth.getSession();

    if (error || !session) {
      return null;
    }

    return session;
  } catch {
    return null;
  }
}

/**
 * Get both user and session
 * @throws Error if not authenticated
 * @returns Promise<AuthResult> User and session
 */
export async function requireAuth(): Promise<AuthResult> {
  const [user, session] = await Promise.all([
    getCurrentUser(),
    getAuthSession(),
  ]);

  return { user, session };
}

/**
 * Get the current user ID
 * @throws Error if not authenticated
 * @returns Promise<string> The user ID
 */
export async function getCurrentUserId(): Promise<string> {
  const user = await getCurrentUser();
  return user.id;
}

/**
 * Get the current user ID, returns null if not authenticated
 * @returns Promise<string | null> The user ID or null
 */
export async function getCurrentUserIdOrNull(): Promise<string | null> {
  const user = await getCurrentUserOrNull();
  return user?.id || null;
}

/**
 * Check if a user is authenticated
 * @returns Promise<boolean> True if authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const user = await getCurrentUserOrNull();
  return user !== null;
}

/**
 * Higher-order function to wrap async functions that require authentication
 * Automatically handles authentication checks and error handling
 * 
 * @example
 * const deleteSession = withAuth(async (userId: string, sessionId: string) => {
 *   // userId is guaranteed to be available
 *   return await db.from('sessions').delete().eq('id', sessionId).eq('user_id', userId);
 * });
 */
export function withAuth<TArgs extends any[], TReturn>(
  fn: (userId: string, ...args: TArgs) => Promise<TReturn>
): (...args: TArgs) => Promise<TReturn> {
  return async (...args: TArgs): Promise<TReturn> => {
    const userId = await getCurrentUserId();
    return fn(userId, ...args);
  };
}

/**
 * Higher-order function that provides the full user object to the wrapped function
 */
export function withAuthUser<TArgs extends any[], TReturn>(
  fn: (user: User, ...args: TArgs) => Promise<TReturn>
): (...args: TArgs) => Promise<TReturn> {
  return async (...args: TArgs): Promise<TReturn> => {
    const user = await getCurrentUser();
    return fn(user, ...args);
  };
}/**
 * Robust sign out that handles local storage and cookies cleanup
 * especially for local development with Supabase CLI
 */
export async function signOut(): Promise<void> {
  try {
    // Before the session is cleared: `logoutOneSignal` deletes this device's
    // player id through RLS, which needs the still-valid session. After
    // `auth.signOut()` it would silently skip the delete and leave the device
    // subscribed to the account that just left.
    await logoutOneSignal();

    // Use local scope to avoid 403 errors in local development
    const { error } = await auth.signOut({ scope: 'local' });

    if (error) {
      if (error.name === 'AuthSessionMissingError' || error.message?.includes('session missing')) {
        console.log('Sign out: No active session found, completing local sign out');
      } else {
        console.warn('Sign out error (non-critical):', error);
      }

      // Manually clear session if API call fails
      clearAllAuthState();
    }
  } catch (error: any) {
    if (error?.name !== 'AuthSessionMissingError') {
      console.warn('Sign out error (non-critical):', error);
    }
    clearAllAuthState();
  }
}

/**
 * Manually clear all authentication state from cookies and local storage
 */
export function clearAllAuthState(): void {
  try {
    const cookieDomain = getCookieDomain();

    // Clear cookies - get all cookies and filter auth-related ones
    const allCookies = Cookies.get();
    Object.keys(allCookies).forEach(name => {
      if (name.includes('auth-token') || name.startsWith('sb-')) {
        // Remove with domain
        Cookies.remove(name, {
          domain: cookieDomain,
          path: '/',
        });

        // Also try without the dot if it has one
        if (cookieDomain.startsWith('.')) {
          Cookies.remove(name, {
            domain: cookieDomain.substring(1),
            path: '/',
          });
        }
      }
    });

    // Clear local storage keys used by Supabase auth
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-')) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {
    console.error('Error clearing auth state:', e);
  }
}

/**
 * Sign in with Google OAuth
 * Redirects to Google for authentication, then back to the app
 *
 * OAuth leaves the page, so anything held in memory — including the action that
 * opened a sign-in gate — is gone by the time the visitor returns. `nextPath`
 * is the one thing that can survive: it rides along in the callback URL and
 * `AuthCallback` navigates there instead of dumping everyone on `/`. Pass the
 * visitor's current `pathname + search` so a gated action puts them back where
 * they were rather than on the home page.
 *
 * The two redirect legs are the API's (`/v1/auth/oauth/google/start` and its
 * callback); this only chooses where to come back to. The API validates that
 * origin against first-party hosts, so an untrusted value is rejected there
 * rather than turning the hand-off into an open redirect.
 */
export async function signInWithGoogle(nextPath?: string): Promise<void> {
  const callbackPath = nextPath
    ? `/auth/callback?next=${encodeURIComponent(nextPath)}`
    : '/auth/callback';

  const { error } = await auth.signInWithOAuth({
    provider: 'google',
    options: {
      // Scopes and `prompt` live on the server, next to the client secret: a
      // client that chose its own scopes could ask for more than sign-in needs.
      redirectTo: getAppUrl(callbackPath),
    },
  });
  if (error) throw error;
}
